<template>
    <section class="embedded-html">
        <div class="inner">
            <h3 :class="titleClass">{{ embedTitle }}</h3>
            <client-only placeholder="Loading...">
                <div
                    ref="embedHtml"
                    v-html="htmlSnippet"
                />
            </client-only>
        </div>
    </section>
</template>
<script setup>
import { useTemplateRef, onMounted, nextTick, computed } from 'vue';

const embedHtml = useTemplateRef('embedHtml');

const props = defineProps({
    contentType: {
        type: [ String, null ],
        required: false,
        default: 'cardList',
    },
    display: {
        type: Object,
        required: false,
        default: () => ({
            component: 'embedded',
            title: '',
            alignment: 'center',
        }),
    },
    internal: {
        type: [ String, null ],
        required: false,
        default: 'Embedded Html',
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    cards: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const embedTitle = computed(() => props.display.title);

const textAlign = computed(() => props.display.alignment);

const titleClass = computed(() => ({
        [`text--${textAlign.value}`]: true,
        'embedded-title': true,
    }));

const htmlSnippet = computed(() => props.display.htmlsnippet);

const activateEmbedScripts = (retries = 10) => {
    nextTick(() => {
        if (embedHtml.value) {
            const scriptElements = [ ...embedHtml.value.querySelectorAll('script') ];

            function recreateScriptElement(scriptElement) {
                const newScript = document.createElement('script');
                [ ...scriptElement.attributes ]
                    .forEach(({ name, value }) => newScript.setAttribute(name, value));
                const newScriptContent = document.createTextNode(scriptElement.innerHTML);
                newScript.appendChild(newScriptContent);

                return newScript;
            }

            // Vue inserts v-html using innerHTML, which blocks execution
            // We'll activate those by recreating them and using replaceChild
            function activateScript(scriptElement) {
                scriptElement.parentNode.replaceChild(recreateScriptElement(scriptElement), scriptElement);
            }

            scriptElements.forEach(activateScript);
            // @see https://nuxtjs.org/docs/features/nuxt-components/#the-client-only-component
        } else if (retries > 0) {
            activateEmbedScripts(retries - 1);
        }
    });
};

onMounted(() => activateEmbedScripts());

</script>
<style scoped>
.embedded-html {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.embedded-html .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}

.embedded-title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 3rem auto 1.5rem;
}
</style>
